import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactElement,
} from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  ArrowLeft,
  ChevronDown,
  Inbox,
  LogOut,
  Mail,
  PanelLeftOpen,
  Phone,
  Pin,
  PinOff,
} from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Separator } from '@/components/atoms/separator';
import { Avatar, AvatarFallback } from '@/components/atoms/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/tooltip';
import {
  useAuthStore,
  useAuthPermissions,
  useAuthFieldMasks,
  useAuthTechnicianId,
  useAuthPermissionScopes,
  useImpersonation,
} from '@/stores/auth.store';
import { useLogout } from '@/hooks/useAuth';
import { useStopImpersonation } from '@/hooks/useImpersonation';
import { useTotalUnread } from '@/stores/inbox.store';
import { useRailPinned, useUiActions } from '@/stores/ui.store';
import { usePhoneNumbers } from '@/lib/messaging/hooks';
import { useInboxes } from '@/lib/messaging/useInboxes';
import { useCompanySettings } from '@/hooks/settings/useCompanySettings';
import { useCostingSettings } from '@/hooks/costing/useCostingSettings';
import { useFeatureFlags } from '@/stores/tenant.store';
import { useNotificationUnreadCount } from '@/stores/notification.store';
import {
  SETTINGS_NAV_COLLAPSED_KEY,
  SETTINGS_NAV_TOGGLE_EVENT,
} from '@/contexts/settings-workspace-layout';
import { formatPhoneDisplay } from '@/lib/contacts/phone';
import { cn } from '@/lib/utils';
import { TEAM_CHAT_PATH, useTeamChatUnreadCount } from '@/components/team-chat/hooks';
import {
  PINNED_NAV_ITEMS,
  SETTINGS_ITEM,
  SETTINGS_SECTION,
  GROWTH_SECTIONS,
  BACK_OFFICE_SECTIONS,
  REPORTING_SECTIONS,
  getSectionForPath,
  isNavItemVisible,
  isNonNavRoute,
  isSettingsPath,
  type NavItem,
} from './nav-config';

// ─── pure active-state helpers (no hooks → safe to call in loops) ───────────

function pathMatches(pathname: string, item: Pick<NavItem, 'path' | 'exact' | 'activePaths'>) {
  if (item.activePaths?.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return true;
  return item.exact
    ? pathname === item.path
    : pathname === item.path || pathname.startsWith(`${item.path}/`);
}

/** Longest-path-wins so "/jobs" isn't active while on "/jobs/new" (a sibling). */
function childActive(pathname: string, item: NavItem, siblings: NavItem[]): boolean {
  if (item.activePaths?.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return true;
  // A retired lens keeps its route but no longer has a child to own it — don't
  // let a shorter sibling claim it (see NON_NAV_ROUTES).
  if (isNonNavRoute(pathname)) return false;
  if (item.exact) return pathname === item.path;
  const matches = pathname === item.path || pathname.startsWith(`${item.path}/`);
  if (!matches) return false;
  return !siblings.some(
    (s) =>
      s.path !== item.path &&
      s.path.length > item.path.length &&
      (pathname === s.path || pathname.startsWith(`${s.path}/`)),
  );
}

// Active = solid charcoal pill; inactive legible, hover = muted. One 13px step.
const ROW =
  'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] font-medium transition-colors';
const rowTint = (active: boolean) =>
  active
    ? 'bg-primary text-primary-foreground'
    : 'text-foreground/75 hover:bg-muted hover:text-foreground';

function Badge({ count, className }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return (
    <span
      className={cn(
        'flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground',
        className,
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function PinnedLeaf({
  item,
  pathname,
  collapsed,
  unread,
}: {
  item: NavItem;
  pathname: string;
  collapsed: boolean;
  unread: number;
}) {
  const Icon = item.icon;
  const active = pathMatches(pathname, item);

  return (
    <Link to={item.path} className={cn(ROW, collapsed && 'justify-center', rowTint(active))}>
      <span className="relative shrink-0">
        <Icon className="size-[18px]" />
        {collapsed ? (
          <Badge count={unread} className="absolute -right-1.5 -top-1.5 text-[9px]" />
        ) : null}
      </span>
      {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
      {!collapsed ? <Badge count={unread} className="ml-auto" /> : null}
    </Link>
  );
}

/** Wrap a rail control so it gets a right-side tooltip ONLY while collapsed.
 *  The TooltipTrigger always wraps the child (same tree in both states) so the
 *  focused element is never reparented/unmounted (keyboard focus preserved).
 *  While expanded the tooltip is force-closed (`open={false}`): otherwise hover
 *  over rows in the expanded rail leaves each tooltip internally open, and they
 *  all pop at once when the rail collapses (and stay stuck, since the pointer has
 *  already left). Collapsed → uncontrolled, hover-driven. */
function RailRow({
  collapsed,
  label,
  children,
}: {
  collapsed: boolean;
  label: string;
  children: ReactElement;
}) {
  // Consistently CONTROLLED (no controlled↔uncontrolled switch): open only when
  // collapsed + hovered. Hover changes are ignored while expanded, and open is
  // reset on expand, so a tooltip can never carry stale open-state into a
  // collapse and pop without a fresh hover.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!collapsed) setOpen(false);
  }, [collapsed]);
  return (
    <Tooltip
      open={collapsed && open}
      onOpenChange={(next) => {
        if (collapsed) setOpen(next);
      }}
    >
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

/** Section header (icon + label + chevron) + its indented children. The children
 *  slide open/closed and are `inert` while closed so they leave the tab order. */
function AccordionSection({
  id,
  label,
  icon: Icon,
  items,
  activePaths,
  collapsed,
  pathname,
  permissions,
  technicianId,
  costingReportingEnabled,
  flags,
  fieldMasks,
  permissionScopes,
  expanded,
  unread,
  onToggle,
}: {
  id: string;
  label: string;
  icon: NavItem['icon'];
  items: NavItem[];
  /** Parent activePaths (pinned items) — routes that count as this section but
   *  aren't children (e.g. Schedule ← /dashboard/board, /dashboard/unscheduled). */
  activePaths?: string[];
  collapsed: boolean;
  pathname: string;
  permissions: string[];
  technicianId: string | null;
  costingReportingEnabled: boolean;
  flags: Record<string, boolean>;
  fieldMasks: readonly string[];
  permissionScopes: Record<string, string>;
  expanded: boolean;
  unread: number;
  onToggle: () => void;
}) {
  const visible = useMemo(
    () =>
      items.filter((c) =>
        isNavItemVisible(c, permissions, {
          technicianId,
          costingReportingEnabled,
          flags,
          fieldMasks,
          permissionScopes,
        }),
      ),
    [
      items,
      permissions,
      technicianId,
      costingReportingEnabled,
      flags,
      fieldMasks,
      permissionScopes,
    ],
  );
  if (visible.length === 0) return null;
  // §3 — a section containing the active route is "marked" (parent activePaths
  // included, since getSectionForPath only scans children). Open → subtle
  // foreground+semibold (the active child carries the pill); else the pill.
  const sectionActive =
    getSectionForPath(pathname) === id ||
    Boolean(activePaths?.some((p) => pathname === p || pathname.startsWith(`${p}/`)));
  const headerTint = sectionActive
    ? expanded && !collapsed
      ? 'text-foreground font-semibold hover:bg-muted'
      : 'bg-primary text-primary-foreground'
    : 'text-foreground/75 hover:bg-muted hover:text-foreground';
  // Children only open when the rail is expanded AND the section is toggled open.
  const childrenOpen = !collapsed && expanded;

  return (
    <div>
      <RailRow collapsed={collapsed} label={label}>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={collapsed ? undefined : expanded}
          className={cn(ROW, headerTint, collapsed ? 'justify-center' : 'pr-1')}
        >
          <Icon className="size-[18px] shrink-0" />
          {!collapsed && <span className="flex-1 truncate text-left">{label}</span>}
          {!collapsed && (
            <ChevronDown
              className={cn(
                'size-3.5 shrink-0 opacity-70 transition-transform duration-200',
                expanded ? 'rotate-0' : '-rotate-90',
              )}
            />
          )}
        </button>
      </RailRow>
      {/* Slide open/closed (grid 0fr→1fr) instead of a hard cut; ~180ms + a slight
          fade. Children stay mounted (clipped) so the close animation runs, but
          `inert` keeps the hidden links out of the tab order + a11y tree.
          prefers-reduced-motion disables the transition. */}
      <div
        inert={!childrenOpen}
        className={cn(
          'grid transition-[grid-template-rows] duration-[180ms] ease-out motion-reduce:transition-none',
          childrenOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div
            className={cn(
              'ml-[18px] mt-0.5 space-y-0.5 border-l pl-2 transition-opacity duration-200 motion-reduce:transition-none',
              childrenOpen ? 'opacity-100' : 'opacity-0',
            )}
          >
            {visible.reduce<React.ReactNode[]>((acc, child, i) => {
              const prevGroup = i > 0 ? visible[i - 1].group : undefined;
              if (child.group && child.group !== prevGroup) {
                acc.push(
                  <p
                    key={`g-${child.group}`}
                    className={cn(
                      'px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60',
                      i === 0 && 'pt-1',
                    )}
                  >
                    {child.group}
                  </p>,
                );
              }
              const active = childActive(pathname, child, visible);
              const ChildIcon = child.icon;
              acc.push(
                <Link key={child.path} to={child.path} className={cn(ROW, rowTint(active))}>
                  <ChildIcon className="size-4 shrink-0" />
                  <span className="flex-1 truncate">{child.label}</span>
                  {child.path === '/dashboard/inbox' ? (
                    <Badge count={unread} className="ml-auto" />
                  ) : null}
                </Link>,
              );
              return acc;
            }, [])}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Inbox section — expandable, hosts the tenant's channels ─────────────────
//
// The section lists the tenant's channels (Inboxes) fetched at runtime — that's
// it, no view filters or legend (those live in the inbox page's toolbar now).
// Because the children are dynamic it can't be a static `AccordionSection`. The
// header itself links to the unfiltered inbox (all conversations); each child
// filters to one channel via `/dashboard/inbox?inboxId=…`.

const INBOX_PATH = '/dashboard/inbox';

function InboxNavSection({
  collapsed,
  expanded,
  onToggle,
  unread,
  pathname,
}: {
  collapsed: boolean;
  expanded: boolean;
  onToggle: () => void;
  unread: number;
  pathname: string;
}) {
  // Outside Inbox, keep this selector referentially stable so search-param
  // writes on list pages do not wake the collapsed rail. Inbox still needs its
  // own search state to preserve the active channel/view links.
  const inboxSearch = useRouterState({
    select: (state) => {
      const currentPath = state.location.pathname;
      return currentPath === INBOX_PATH || currentPath.startsWith(`${INBOX_PATH}/`)
        ? state.location.search
        : undefined;
    },
  });
  const inboxesQuery = useInboxes();
  const channels = inboxesQuery.data ?? [];

  const onInbox = pathname === INBOX_PATH || pathname.startsWith(`${INBOX_PATH}/`);
  // Only trust the current search when we're actually on the inbox route — on
  // any other page `location.search` belongs to that route.
  const search = (inboxSearch ?? {}) as unknown as {
    view?: string;
    inboxId?: string;
    mode?: string;
    q?: string;
  };
  const curView = search.view ?? 'all';
  const curInboxId = search.inboxId;
  const callsMode = search.mode === 'calls';

  // Collapsed rail: children can't render (no width), so Inbox degrades to a
  // single icon link with the unread count overlaid — same as the other leaf
  // pinned items.
  if (collapsed) {
    return (
      <RailRow collapsed={collapsed} label="Inbox">
        <Link
          to={INBOX_PATH}
          search={{ view: curView, ...(curInboxId ? { inboxId: curInboxId } : {}) } as never}
          className={cn(ROW, 'justify-center', rowTint(onInbox))}
        >
          <span className="relative shrink-0">
            <Inbox className="size-[18px]" />
            {unread > 0 ? (
              <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">
                {unread > 99 ? '99+' : unread}
              </span>
            ) : null}
          </span>
        </Link>
      </RailRow>
    );
  }

  // Header links to the unfiltered inbox (all conversations) and is "active"
  // when we're on the inbox with no channel selected. The chevron is a separate
  // toggle so expanding to see channels doesn't force a navigation.
  const headerActive = onInbox && !curInboxId && !callsMode;
  const headerTint = headerActive
    ? 'bg-primary text-primary-foreground'
    : onInbox
      ? 'text-foreground font-semibold hover:bg-muted'
      : 'text-foreground/75 hover:bg-muted hover:text-foreground';

  return (
    <div>
      <div className="flex items-center gap-0.5">
        <Link
          to={INBOX_PATH}
          search={{ view: curView } as never}
          className={cn(ROW, 'flex-1', headerTint)}
        >
          <Inbox className="size-[18px] shrink-0" />
          <span className="flex-1 truncate text-left">Inbox</span>
          <Badge count={unread} />
        </Link>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse channels' : 'Show channels'}
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-foreground/60 hover:bg-muted hover:text-foreground"
        >
          <ChevronDown
            className={cn(
              'size-3.5 transition-transform duration-200',
              expanded ? 'rotate-0' : '-rotate-90',
            )}
          />
        </button>
      </div>
      <div
        inert={!expanded}
        className={cn(
          'grid transition-[grid-template-rows] duration-[180ms] ease-out motion-reduce:transition-none',
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div
            className={cn(
              'ml-[18px] mt-0.5 space-y-0.5 border-l pl-2 transition-opacity duration-200 motion-reduce:transition-none',
              expanded ? 'opacity-100' : 'opacity-0',
            )}
          >
            {channels.length === 0 ? (
              <p className="px-2 py-1.5 text-[11px] text-muted-foreground/70">No channels yet</p>
            ) : (
              channels.map((c) => {
                const ChannelIcon = c.kind === 'EMAIL' ? Mail : Phone;
                const active = onInbox && !callsMode && curInboxId === c.id;
                return (
                  <Link
                    key={c.id}
                    to={INBOX_PATH}
                    search={{ view: curView, inboxId: c.id } as never}
                    className={cn(ROW, rowTint(active))}
                    title={c.name}
                  >
                    <ChannelIcon className="size-4 shrink-0" />
                    <span className="flex-1 truncate">{c.name}</span>
                    <Badge count={c.unreadCount} />
                  </Link>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Nearest scrollable ancestor of `el` (the rail's overflow container). */
function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

/** §4 — Settings drill-in (expanded only): "← Settings" + the SETTINGS_SECTION
 *  items grouped by `group`, each with its icon, scrollable, active = pill.
 *  Exported for the scroll-retention regression test (NOT-507). */
export function SettingsPanel({
  pathname,
  permissions,
  technicianId,
  costingReportingEnabled,
  flags,
  onBack,
  scrollMemory,
}: {
  pathname: string;
  permissions: string[];
  technicianId: string | null;
  costingReportingEnabled: boolean;
  flags: Record<string, boolean>;
  onBack: () => void;
  /** Parent-owned last scroll offset, so it survives collapse (this remounts). */
  scrollMemory: MutableRefObject<number | null>;
}) {
  const visible = useMemo(
    () =>
      SETTINGS_SECTION.items.filter((i) =>
        isNavItemVisible(i, permissions, { technicianId, costingReportingEnabled, flags }),
      ),
    [permissions, technicianId, costingReportingEnabled, flags],
  );
  const rootRef = useRef<HTMLButtonElement>(null);
  const activeRef = useRef<HTMLAnchorElement>(null);
  const activePath = useMemo(
    () => visible.find((item) => childActive(pathname, item, visible))?.path,
    [visible, pathname],
  );
  // This panel unmounts when the rail collapses and remounts on the next expand,
  // so its scroll container would otherwise reset to the top. Restore the offset
  // the user left it at (retention), and keep tracking scrolls. On the very
  // first visit there's nothing saved yet, so reveal the active row instead of
  // dumping them at the top. `activePath` in the deps re-runs the reveal once a
  // permission-gated active row actually renders.
  useLayoutEffect(() => {
    const scroller = getScrollParent(rootRef.current);
    if (!scroller) return;

    if (scrollMemory.current != null) {
      scroller.scrollTop = scrollMemory.current;
    } else if (activeRef.current) {
      // First visit: reveal the active row and record where that landed. Only
      // once it exists — a permission-gated row may render a beat later, and
      // leaving the memory null lets the activePath re-run reveal it then.
      activeRef.current.scrollIntoView({ block: 'nearest' });
      scrollMemory.current = scroller.scrollTop;
    }

    const onScroll = () => {
      scrollMemory.current = scroller.scrollTop;
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [scrollMemory, activePath]);
  return (
    <>
      <button
        ref={rootRef}
        type="button"
        onClick={onBack}
        className={cn(ROW, 'text-foreground/75 hover:bg-muted hover:text-foreground')}
      >
        <ArrowLeft className="size-[18px] shrink-0" />
        <span className="truncate font-semibold">Settings</span>
      </button>
      <Separator className="my-1" />
      {visible.reduce<React.ReactNode[]>((acc, item, i) => {
        const prevGroup = i > 0 ? visible[i - 1].group : undefined;
        if (item.group && item.group !== prevGroup) {
          acc.push(
            <p
              key={`g-${item.group}`}
              className={cn(
                'px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60',
                i === 0 && 'pt-1',
              )}
            >
              {item.group}
            </p>,
          );
        }
        const ItemIcon = item.icon;
        const active = childActive(pathname, item, visible);
        acc.push(
          <Link
            key={item.path}
            ref={active ? activeRef : undefined}
            to={item.path}
            className={cn(ROW, rowTint(active))}
          >
            <ItemIcon className="size-4 shrink-0" />
            <span className="flex-1 truncate">{item.label}</span>
          </Link>,
        );
        return acc;
      }, [])}
    </>
  );
}

/**
 * The classes the rail applies while it FLOATS over the page — hovered or
 * keyboard-focused, and not pinned.
 *
 * It has to sit above portalled popover content (`z-50` in
 * `components/atoms/popover`), not below it. A page dropdown wide enough to
 * reach the left edge — the pricebook picker on a settings form with the
 * settings menu hidden — otherwise painted straight over the nav the pointer
 * was on, leaving the nav rows unreadable and the dropdown cut in half.
 *
 * Above a MODAL is not a risk: a dialog's overlay swallows the hover, so the
 * rail never expands behind one.
 *
 * The whole class string lives here, not just the layer, so the branch below
 * has nothing left to state on its own and the test has one thing to check.
 */
export const FLOATING_RAIL_CLASS = 'absolute inset-y-0 left-0 z-[60] w-60 shadow-lg';

/**
 * Single sidebar nav (replaces the icon rail + secondary panel). Collapse/pin:
 * collapsed = icons only (w-[60px]); hover OR keyboard-focus expands to
 * icons+labels (w-60, floating); the pin fixes it expanded. Built as ONE tree
 * collapsed via CSS (labels hidden, tooltips shown) — never swapping elements —
 * so keyboard focus survives expand/collapse. Sections expand their children
 * downward (one open at a time, auto-opened to the active route). Settings is a
 * drill-in (§4).
 */
export function SidebarNav() {
  // The rail's active state depends on the path, not the page's search params.
  // Subscribing to the full location re-rendered every Radix tooltip whenever a
  // list wrote its debounced search to the URL. With React 19, the nested
  // `asChild` refs can then synchronously detach/reattach and trip React's
  // maximum-update-depth boundary, remounting the page mid-search.
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navigate = useNavigate();
  const permissions = useAuthPermissions();
  const fieldMasks = useAuthFieldMasks();
  const technicianId = useAuthTechnicianId();
  const permissionScopes = useAuthPermissionScopes();
  const costingSettings = useCostingSettings();
  const costingReportingEnabled = costingSettings.data?.enableReporting === true;
  const flags = useFeatureFlags();
  const totalUnread = useTotalUnread();
  const notificationUnread = useNotificationUnreadCount();
  const teamChatUnread = useTeamChatUnreadCount();
  const pinned = useRailPinned();
  const { toggleRailPinned } = useUiActions();
  const [hovered, setHovered] = useState(false);
  // Keyboard focus expands the rail like hover does — without it a keyboard user
  // on the collapsed rail couldn't reach labels, children or the pin. Safe now
  // because expansion only toggles CSS (no element swap → focus is preserved).
  const [kbFocus, setKbFocus] = useState(false);
  const railExpanded = pinned || hovered || kbFocus;
  const collapsed = !railExpanded;
  const [settingsNavCollapsed, setSettingsNavCollapsed] = useState(readSettingsNavigationCollapsed);

  useEffect(() => {
    const syncSettingsNavigation = (event: Event) => {
      const requestedState = (event as CustomEvent<boolean>).detail;
      setSettingsNavCollapsed((current) =>
        typeof requestedState === 'boolean' ? requestedState : !current,
      );
    };

    window.addEventListener(SETTINGS_NAV_TOGGLE_EVENT, syncSettingsNavigation);
    return () => window.removeEventListener(SETTINGS_NAV_TOGGLE_EVENT, syncSettingsNavigation);
  }, []);

  // NOT-549 — brand the sidebar header with the workspace's own company (from
  // Company Profile), same name+logo shown on the user footer / customer docs.
  // Fallbacks: company name → "Notifi"; uploaded logoUrl → company initials.
  const companySettings = useCompanySettings();
  const companyName = companySettings.data?.name?.trim() || 'Notifi';
  const companyLogoUrl = companySettings.data?.logoUrl?.trim() || null;
  const companyInitials =
    companyName
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word[0] ?? '')
      .join('')
      .toUpperCase() || 'N';
  // Fall back to the initials mark if a set logo URL fails to load (404/expired
  // presign). Reset when the URL changes so a new upload gets a fresh attempt.
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  useEffect(() => {
    setLogoLoadFailed(false);
  }, [companyLogoUrl]);
  const showCompanyLogo = Boolean(companyLogoUrl) && !logoLoadFailed;
  // Clicking the company logo opens the Getting Started checklist for setup-capable
  // admins/owners; everyone else (e.g. technicians, who never see onboarding) keeps
  // the dashboard. Mirrors the `company:edit` gate on the Getting started nav item.
  const brandTo = permissions.includes('company:edit')
    ? '/dashboard/onboarding'
    : '/dashboard/home';

  // A section owns the route via its children (getSectionForPath) OR a pinned
  // item's parent activePaths (e.g. Schedule ← /board, not a child).
  const activeSectionId =
    getSectionForPath(pathname) ??
    (pathname === '/dashboard/inbox' || pathname.startsWith('/dashboard/inbox/')
      ? 'inbox'
      : null) ??
    PINNED_NAV_ITEMS.find(
      (i) =>
        i.sectionId && i.activePaths?.some((p) => pathname === p || pathname.startsWith(`${p}/`)),
    )?.sectionId ??
    null;
  // One section open at a time; auto-open the active route's section.
  const [expanded, setExpanded] = useState<string | null>(activeSectionId);
  useEffect(() => {
    if (activeSectionId) setExpanded(activeSectionId);
  }, [activeSectionId]);

  const openSettings = () => {
    if (isSettingsPath(pathname)) {
      window.dispatchEvent(
        new CustomEvent(SETTINGS_NAV_TOGGLE_EVENT, { detail: !settingsNavCollapsed }),
      );
      return;
    }
    navigate({ to: SETTINGS_ITEM.path as never });
  };
  const settingsPathActive = isSettingsPath(pathname);
  const settingsControlLabel = settingsPathActive
    ? settingsNavCollapsed
      ? 'Show settings menu'
      : 'Hide settings menu'
    : SETTINGS_ITEM.label;

  const canShow = useCallback(
    (item: NavItem) =>
      isNavItemVisible(item, permissions, {
        technicianId,
        costingReportingEnabled,
        flags,
        fieldMasks,
        permissionScopes,
      }),
    [permissions, technicianId, costingReportingEnabled, flags, fieldMasks, permissionScopes],
  );
  // A section-style pinned item shows if the parent OR any child is visible — a
  // user can hold a child permission without the parent's (bookings:view without
  // jobs:view should still surface Requests). Leaves gate normally.
  const visiblePinned = useMemo(
    () =>
      PINNED_NAV_ITEMS.filter((item) =>
        item.children?.length && item.sectionId
          ? canShow(item) || item.children.some(canShow)
          : canShow(item),
      ),
    [canShow],
  );
  // NOT-567 — two clusters separated by a divider: growth (Sales/Marketing/Iris)
  // then back office (Billing/Team/Resources).
  const visibleGrowth = useMemo(
    () => GROWTH_SECTIONS.filter((s) => s.items.some(canShow)),
    [canShow],
  );
  const visibleBackOffice = useMemo(
    () => BACK_OFFICE_SECTIONS.filter((s) => s.items.some(canShow)),
    [canShow],
  );
  const visibleReporting = useMemo(
    () => REPORTING_SECTIONS.filter((s) => s.items.some(canShow)),
    [canShow],
  );

  // §2 (Gabo) — clicking a section ONLY expands/collapses it; navigation happens
  // when a child is clicked. Opening one collapses the others (single open id).
  const toggle = (id: string) => setExpanded((e) => (e === id ? null : id));

  return (
    <div
      className={cn('relative h-full shrink-0', pinned ? 'w-60' : 'w-[60px]')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={(e) => {
        // Expand only on KEYBOARD focus (:focus-visible). A mouse click also
        // focuses the link/section, which would otherwise pin the rail open with
        // its labels after the pointer leaves — only keyboard nav should expand.
        const el = e.target as HTMLElement;
        if (typeof el.matches === 'function' && el.matches(':focus-visible')) setKbFocus(true);
      }}
      onBlurCapture={(e) => {
        // Collapse again when focus leaves the rail entirely.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setKbFocus(false);
      }}
    >
      <div
        className={cn(
          'flex h-full flex-col overflow-hidden border-r bg-background transition-[width] duration-200',
          pinned ? 'w-60' : railExpanded ? FLOATING_RAIL_CLASS : 'w-[60px]',
        )}
      >
        {/* Brand */}
        <div
          className={cn(
            'flex h-14 shrink-0 items-center px-3',
            collapsed ? 'justify-center' : 'justify-between',
          )}
        >
          {/* Logo mark only — no company wordmark. aria-label carries the name
              for screen readers, and title surfaces it on hover, since the mark
              itself is decorative. */}
          <Link
            to={brandTo}
            className="flex min-w-0 items-center gap-2"
            aria-label={companyName}
            title={companyName}
          >
            {showCompanyLogo ? (
              <img
                src={companyLogoUrl ?? undefined}
                alt=""
                onError={() => setLogoLoadFailed(true)}
                className="size-8 shrink-0 rounded-md object-contain"
              />
            ) : (
              <span
                aria-hidden
                className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary text-sm font-semibold text-primary-foreground"
              >
                {companyInitials}
              </span>
            )}
          </Link>
          {!collapsed && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 text-muted-foreground"
              onClick={toggleRailPinned}
              aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
            >
              {pinned ? <PinOff className="size-4" /> : <Pin className="size-4" />}
            </Button>
          )}
        </div>

        <div className="flex flex-1 flex-col overflow-y-auto px-2 py-2">
          <div className="flex flex-col gap-0.5">
            <>
              {visiblePinned.map((item) =>
                item.path === '/dashboard/inbox' ? (
                  <InboxNavSection
                    key={item.path}
                    collapsed={collapsed}
                    expanded={expanded === 'inbox'}
                    onToggle={() => toggle('inbox')}
                    unread={totalUnread}
                    pathname={pathname}
                  />
                ) : item.children?.length && item.sectionId ? (
                  <AccordionSection
                    key={item.sectionId}
                    id={item.sectionId}
                    label={item.label}
                    icon={item.icon}
                    items={item.children}
                    activePaths={item.activePaths}
                    collapsed={collapsed}
                    pathname={pathname}
                    permissions={permissions}
                    technicianId={technicianId}
                    costingReportingEnabled={costingReportingEnabled}
                    flags={flags}
                    fieldMasks={fieldMasks}
                    permissionScopes={permissionScopes}
                    expanded={expanded === item.sectionId}
                    unread={totalUnread}
                    onToggle={() => toggle(item.sectionId!)}
                  />
                ) : (
                  <RailRow key={item.path} collapsed={collapsed} label={item.label}>
                    <PinnedLeaf
                      item={item}
                      pathname={pathname}
                      collapsed={collapsed}
                      unread={
                        item.path === '/dashboard/inbox'
                          ? totalUnread
                          : item.path === '/dashboard/notifications'
                            ? notificationUnread
                            : item.path === TEAM_CHAT_PATH
                              ? teamChatUnread
                              : 0
                      }
                    />
                  </RailRow>
                ),
              )}

              {visiblePinned.length > 0 &&
                (visibleGrowth.length > 0 ||
                  visibleBackOffice.length > 0 ||
                  visibleReporting.length > 0) && (
                  <Separator className={collapsed ? 'mx-auto my-2 w-8' : 'my-2'} />
                )}

              {[visibleGrowth, visibleBackOffice, visibleReporting].map((cluster, clusterIdx) => (
                <Fragment key={['growth', 'back-office', 'reporting'][clusterIdx]}>
                  {clusterIdx > 0 &&
                    cluster.length > 0 &&
                    [visibleGrowth, visibleBackOffice]
                      .slice(0, clusterIdx)
                      .some((previous) => previous.length > 0) && (
                      <Separator className={collapsed ? 'mx-auto my-2 w-8' : 'my-2'} />
                    )}
                  {cluster.map((section) => (
                    <AccordionSection
                      key={section.id}
                      id={section.id}
                      label={section.label}
                      icon={section.icon}
                      items={section.items}
                      collapsed={collapsed}
                      pathname={pathname}
                      permissions={permissions}
                      technicianId={technicianId}
                      costingReportingEnabled={costingReportingEnabled}
                      flags={flags}
                      fieldMasks={fieldMasks}
                      permissionScopes={permissionScopes}
                      expanded={expanded === section.id}
                      unread={totalUnread}
                      onToggle={() => toggle(section.id)}
                    />
                  ))}
                </Fragment>
              ))}
            </>
          </div>
        </div>

        {/* Settings entry + user footer */}
        <div className="flex shrink-0 flex-col gap-1 px-2 pb-2">
          <Separator className={collapsed ? 'mx-auto my-1 w-8' : 'my-1'} />
          <RailRow collapsed={collapsed} label={settingsControlLabel}>
            <button
              type="button"
              onClick={openSettings}
              aria-label={settingsControlLabel}
              className={cn(ROW, 'h-8', collapsed && 'justify-center', rowTint(settingsPathActive))}
            >
              {settingsPathActive && settingsNavCollapsed ? (
                <PanelLeftOpen className="size-[18px] shrink-0" />
              ) : (
                <SETTINGS_ITEM.icon className="size-[18px] shrink-0" />
              )}
              {!collapsed && <span className="truncate">{settingsControlLabel}</span>}
            </button>
          </RailRow>
          <Separator className={collapsed ? 'mx-auto my-1 w-8' : 'my-1'} />
          <UserFooter collapsed={collapsed} />
        </div>
      </div>
    </div>
  );
}

function readSettingsNavigationCollapsed(): boolean {
  try {
    return localStorage.getItem(SETTINGS_NAV_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

// Mirrors LAST_EMAIL_KEY in the login / invite flows.
function readLastLoginEmail(): string | null {
  try {
    return localStorage.getItem('notifi:last-email');
  } catch {
    return null;
  }
}

/** Avatar + identity + logout (single tree; identity hidden when collapsed). */
function UserFooter({ collapsed }: { collapsed: boolean }) {
  const userId = useAuthStore((s) => s.userId);
  const storeEmail = useAuthStore((s) => s.email);
  const normalLogout = useLogout();
  const permissions = useAuthPermissions();
  const impersonation = useImpersonation();
  const stopImpersonation = useStopImpersonation();
  const companyQuery = useCompanySettings();

  const email = storeEmail ?? (impersonation.active ? null : readLastLoginEmail());
  const orgName = companyQuery.data?.name?.trim() || 'Organization';
  const userLabel = email ?? userId ?? 'User';
  const initialsSource = email ? email.split('@')[0] : (userId ?? 'U');
  const initials = initialsSource.slice(0, 2).toUpperCase();
  const logout = impersonation.active ? () => void stopImpersonation() : normalLogout;

  const canViewNumbers = permissions.includes('messages:view');
  // NOT-504: skip the fetch entirely without messages:view — passing `{}` still
  // fired the request (a guaranteed 403 for non-messaging roles like accountant).
  const phoneNumbersQuery = usePhoneNumbers(
    { status: 'ACTIVE', pageSize: 5 },
    { enabled: canViewNumbers },
  );
  const accountNumber = canViewNumbers
    ? (phoneNumbersQuery.data?.data.find(
        (n) => n.smsEnabled && (n.purpose === 'SHARED' || n.purpose === 'RECEPTIONIST'),
      )?.e164 ?? phoneNumbersQuery.data?.data[0]?.e164)
    : undefined;
  const accountNumberDisplay = accountNumber ? formatPhoneDisplay(accountNumber) : null;

  return (
    <div className="relative flex min-h-18 items-center justify-between gap-2 px-2 py-1">
      <div className="flex min-w-0 items-center gap-2">
        <RailRow collapsed={collapsed} label={`${userLabel} · ${orgName}`}>
          <div
            className={cn(
              'flex size-7 shrink-0 items-center justify-center',
              collapsed ? 'absolute top-1 left-1/2 -translate-x-1/2' : 'relative',
            )}
          >
            <Avatar className="size-7">
              <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
            </Avatar>
            {impersonation.active && (
              <span
                aria-hidden="true"
                className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-red-600 ring-2 ring-background"
              />
            )}
          </div>
        </RailRow>
        {/* Keep identity in the layout so expanding the rail cannot move Settings
            between pointerdown and click, especially when dismissing a popover. */}
        <div className={cn('min-w-0', collapsed && 'invisible')} aria-hidden={collapsed}>
          {impersonation.active && (
            <span className="mb-0.5 inline-flex items-center rounded-sm bg-red-600 px-1 py-px text-[9px] font-bold uppercase tracking-wider text-white">
              Impersonating
            </span>
          )}
          <p className="truncate text-xs font-medium text-foreground">{userLabel}</p>
          <p className="truncate text-[10px] text-muted-foreground">{orgName}</p>
          {impersonation.active && impersonation.impersonatedBy && (
            <p className="truncate text-[10px] text-muted-foreground">
              as: {impersonation.impersonatedBy}
            </p>
          )}
          {accountNumberDisplay && (
            <p className="mt-0.5 flex items-center gap-1 truncate text-[10px] text-muted-foreground">
              <Phone className="size-2.5 shrink-0" />
              <span className="truncate">{accountNumberDisplay}</span>
            </p>
          )}
        </div>
      </div>
      <RailRow
        collapsed={collapsed}
        label={impersonation.active ? 'Stop impersonating' : 'Log out'}
      >
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'size-7 shrink-0 text-muted-foreground hover:text-destructive',
            collapsed && 'absolute bottom-1 left-1/2 -translate-x-1/2',
          )}
          onClick={logout}
          aria-label={impersonation.active ? 'Stop impersonating' : 'Log out'}
        >
          <LogOut className="size-3.5" />
        </Button>
      </RailRow>
    </div>
  );
}
